import { ArrowDownTrayIcon, EyeIcon, MagnifyingGlassIcon, StarIcon } from '@heroicons/react/24/outline';
import { Avatar, Badge, Box, Button, Checkbox, Group, Paper, Stack, Text, Transition } from '@mantine/core';
import { memo, SVGProps, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BACKEND_API_URI } from '../../auth/msalConfig';
import { BrandColor } from '../../constants/colorPalette';
import { ROUTES } from '../../constants/routes';
import { CVDownloadMenu } from '../../features/CVEditor/components/CVDownloadMenu';
import { Person, PersonInfo } from '../../models/person.model';
import { ConnectedPersonActionsMenu, PersonActionsMenu } from './PersonActionsMenu';
import { ScoreGauge } from './ScoreGauge';

// Create typed icon components to fix TypeScript errors
const IconMagnifyingGlass = (props: SVGProps<SVGSVGElement>) => <MagnifyingGlassIcon {...props} />;
const IconEye = (props: SVGProps<SVGSVGElement>) => <EyeIcon {...props} />;
const IconArrowDownTray = (props: SVGProps<SVGSVGElement>) => <ArrowDownTrayIcon {...props} />;
const IconStar = (props: SVGProps<SVGSVGElement>) => <StarIcon {...props} />;

interface PersonCardProps {
  id: number;
  name: string;
  title?: string;
  lastUpdated?: string;
  isUnassigned?: boolean;
  hasAvailabilityDate?: boolean;
  availableDate?: string;
  isInactive?: boolean;
  phone?: string;
  email?: string;
  profilePhoto?: string;
  finalScoring?: number;
  officeLocation?: string;
  onDownloadCV?: (format?: string) => void;
  onCopyToProposal?: () => void; // Keeping for backward compatibility
  onClick?: () => void; // For CV preview
  onModify?: (e: React.MouseEvent) => void;
  onAssignmentChange?: (e: React.MouseEvent) => void;
  onActivityChange?: (e: React.MouseEvent) => void;
  onDelete?: (e: React.MouseEvent) => void;
  person?: Person | PersonInfo; // Add the person object for ConnectedPersonActionsMenu
  useConnectedActions?: boolean; // Whether to use the connected version of actions menu
  onRefresh?: () => void; // Callback when an action refreshes data
  selectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (id: number) => void;
}

function PersonCardComponent({
  id,
  name,
  title,
  lastUpdated = "Unknown",
  isUnassigned = false,
  hasAvailabilityDate = false,
  availableDate,
  isInactive = false,
  phone,
  email,
  profilePhoto,
  finalScoring,
  officeLocation,
  onDownloadCV,
  onClick,
  onModify,
  onAssignmentChange,
  onActivityChange,
  onDelete,
  person,
  useConnectedActions = false,
  onRefresh,
  selectionMode = false,
  isSelected = false,
  onToggleSelect,
}: PersonCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);

  useEffect(() => {
    setImageLoadFailed(false);
  }, [profilePhoto]);

  const fullImageUrl = useMemo(() => {
    if (imageLoadFailed || !profilePhoto || typeof profilePhoto !== 'string') {
      return undefined;
    }

    if (profilePhoto.startsWith('data:image')) {
      return profilePhoto;
    }

    if (profilePhoto.match(/^[A-Za-z0-9+/=]+$/)) {
      return `data:image/jpeg;base64,${profilePhoto}`;
    }

    if (profilePhoto.startsWith('http://') || profilePhoto.startsWith('https://')) {
      return profilePhoto;
    }

    if (profilePhoto.startsWith('/')) {
      const cleanPath = profilePhoto.replace(/^\/api\/[^\/]+/, '');
      return `${BACKEND_API_URI}${cleanPath}`;
    }

    return `${BACKEND_API_URI}/images/${profilePhoto}`;
  }, [profilePhoto, imageLoadFailed]);

  // Create initials from name (first letter of first name and first letter of last name)
  const getInitials = useCallback((name: string) => {
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`;
    }
    return nameParts[0].charAt(0);
  }, []);

  // Generate email address in format firstname.lastname@soprasteria.com
  const getEmailAddress = useCallback((name: string) => {
    // Always generate email in the format firstname.lastname@soprasteria.com regardless of provided email
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      const firstName = nameParts[0].toLowerCase();
      const lastName = nameParts[nameParts.length - 1].toLowerCase();
      // Format: firstname.lastname@soprasteria.com (e.g., daniel.klosek@soprasteria.com)
      return `${firstName}.${lastName}@soprasteria.com`;
    }
    return `${name.toLowerCase().replace(' ', '.')}@soprasteria.com`;
  }, []);

  const initials = getInitials(name);
  const emailAddress = email || getEmailAddress(name);

  // Navigate to CV detail view or use custom click handler
  const handleCardClick = (e: React.MouseEvent) => {
    if (selectionMode) {
      e.preventDefault();
      e.stopPropagation();
      onToggleSelect?.(id);
      return;
    }
    // Prevent navigation if the button was clicked
    if (e.target instanceof HTMLElement &&
      (e.target.closest('button') || e.target.tagName === 'BUTTON' || e.target.closest('a') || e.target.tagName === 'A')) {
      return;
    }

    // Call the preview handler if provided
    if (onClick) {
      onClick();
    }
  };

  // Handle image loading error
  const handleImageError = () => {
    setImageLoadFailed(true);
  };

  // Add a formatDate function to format dates in a user-friendly way
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not specified';

    const date = new Date(dateString);

    // Check if it's the default SQL date (0001-01-01)
    if (date.getFullYear() <= 1) {
      return 'Not specified';
    }

    // Format in a shorter way to fit in badge: MMM D, YYYY (e.g., Jul 2, 2023)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Handle download button click
  const handleDownloadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // If onDownloadCV is provided, it's just a compatibility function
    // for older code. The user will be taken to a download action from there.
  };

  // Render either connected or regular PersonActionsMenu based on prop
  const renderActionsMenu = () => {
    if (useConnectedActions && person) {
      return (
        <ConnectedPersonActionsMenu
          person={person}
          isUnassigned={isUnassigned}
          isInactive={isInactive}
          onModify={onModify}
          onDelete={onDelete}
          variant="button"
          fullWidth={true}
          onRefresh={onRefresh}
        />
      );
    } else {
      return (
        <PersonActionsMenu
          isUnassigned={isUnassigned}
          isInactive={isInactive}
          onModify={onModify}
          onAssignmentChange={onAssignmentChange}
          onActivityChange={onActivityChange}
          onDelete={onDelete}
          variant="button"
          fullWidth={true}
        />
      );
    }
  };

  return (
    <>
      <Paper
        shadow="xs"
        p="md"
        radius="md"
        withBorder
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: selectionMode ? 'pointer' : 'pointer',
          position: 'relative',
          overflow: 'visible',
          outline: isSelected ? '2px solid var(--mantine-color-brand-5)' : undefined,
          outlineOffset: isSelected ? '2px' : undefined,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleCardClick}
      >
        {/* Selection checkbox (replaces score ring position in selection mode) */}
        {selectionMode && (
          <Box
            style={{
              position: 'absolute',
              top: -10,
              left: 10,
              zIndex: 3,
            }}
            onClick={(e) => { e.stopPropagation(); onToggleSelect?.(id); }}
          >
            <Checkbox
              checked={isSelected}
              onChange={() => onToggleSelect?.(id)}
              aria-label={`Select ${name}`}
              size="md"
              styles={{ input: { cursor: 'pointer' } }}
            />
          </Box>
        )}

        {/* Scoring indicator in top left */}
        {!selectionMode && (
          <Box style={{
            position: 'absolute',
            top: -10,
            left: 10,
            zIndex: 2
          }}>
            <ScoreGauge score={finalScoring} emptyLabel="NoCV" />
          </Box>
        )}

        {/* Status badges - position at top of card with enough space */}
        <Stack gap={2} mb={4} style={{ minHeight: 22 }}>
          <Group gap={4} justify="right" wrap="nowrap">
            {hasAvailabilityDate && availableDate && new Date(availableDate).getFullYear() > 1 && (
              <Badge
                color="orange.9"
                variant="filled"
                radius="sm"
                size="sm"
                style={{
                  textTransform: 'none',
                  position: 'absolute',
                  top: -6,
                  right: 16,
                  zIndex: 2
                }}
              >
                Available {formatDate(availableDate)}
              </Badge>
            )}

            {isInactive && (
              <Badge
                color="gray.7"
                variant="filled"
                radius="sm"
                size="xs"
                style={{
                  textTransform: 'none'
                }}
              >
                Inactive
              </Badge>
            )}

            {isUnassigned && (
              <Badge
                color="orange"
                variant="filled"
                radius="sm"
                size="xs"
                style={{ textTransform: 'none' }}
              >
                Unassigned
              </Badge>
            )}
          </Group>
        </Stack>

        <Box style={{ display: 'flex', gap: '1rem', flex: 1 }}>
          <Box style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Text fw={600} size="lg" title={name} style={{ wordBreak: 'break-word' }}>{name}</Text>
            {title && <Text size="sm" c="dimmed" lineClamp={1} title={title}>{title}</Text>}

            {/* Conditional content based on hover state */}
            <Box mt="xs">
              <>
                <Text size="sm" c="dimmed">CV last updated:</Text>
                <Text size="sm">{lastUpdated}</Text>
              </>
              <>
                {phone && <Text size="sm" lineClamp={1} title={phone}>{phone}</Text>}
                <Text size="sm" lineClamp={1} title={emailAddress} style={{ marginTop: 'auto' }}>{emailAddress}</Text>
                {officeLocation && <Text size="sm" c="dimmed" lineClamp={1} title={officeLocation}>📍 {officeLocation}</Text>}
              </>
            </Box>
          </Box>

          <Box style={{ width: 70, height: 70, flexShrink: 0, position: 'relative' }}>
            <Avatar
              size={70}
              radius="xl"
              color="brand"
              variant="transparent"
              src={fullImageUrl}
              onError={handleImageError}
            >
              {!fullImageUrl && initials}
            </Avatar>

            {/* Magnifying glass overlay on hover */}
            <Transition mounted={isHovered} transition="fade" duration={200}>
              {(styles: React.CSSProperties) => (
                <Box
                  style={{
                    ...styles,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0, 0, 0, 0.5)',
                    borderRadius: '100%',
                    cursor: 'pointer',
                  }}
                >
                  <IconMagnifyingGlass width={24} color="white" />
                </Box>
              )}
            </Transition>
          </Box>
        </Box>

        {!selectionMode && <Box mt="auto" pt="lg">
          {/* First row: Details and My Skills buttons */}
          <Group grow gap={8} mb="xs">
            <Button
              component={Link}
              to={ROUTES.CV_DETAILS(id)}
              variant="light"
              color="gray"
              size="xs"
              leftSection={<IconEye width={16} />}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              title="Click to view, Ctrl+Click for new tab"
            >
              Details
            </Button>

            <Button
              component={Link}
              to={ROUTES.PERSON_SKILLS(id)}
              variant="light"
              color={BrandColor.COMPLIMENTARY_BLUE}
              size="xs"
              leftSection={<IconStar width={16} />}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              title="Click to view, Ctrl+Click for new tab"
            >
              Skills
            </Button>
          </Group>

          {/* Second row: Download and Actions */}
          <Group grow gap={8}>
            {onDownloadCV ? (
              <CVDownloadMenu
                personId={id}
                userName={name}
                buttonProps={{
                  size: 'xs',
                  variant: 'light',
                  children: 'Download',
                  color: 'brand'
                }}
              />
            ) : (
              <Button
                variant="light"
                color="brand"
                size="xs"
                leftSection={<IconArrowDownTray width={16} />}
                onClick={handleDownloadClick}
              >
                Download
              </Button>
            )}

            {renderActionsMenu()}
          </Group>
        </Box>}
      </Paper>
    </>
  );
}

export const PersonCard = memo(PersonCardComponent, (prevProps, nextProps) => {
  return (
    prevProps.id === nextProps.id &&
    prevProps.name === nextProps.name &&
    prevProps.title === nextProps.title &&
    prevProps.lastUpdated === nextProps.lastUpdated &&
    prevProps.isUnassigned === nextProps.isUnassigned &&
    prevProps.hasAvailabilityDate === nextProps.hasAvailabilityDate &&
    prevProps.availableDate === nextProps.availableDate &&
    prevProps.isInactive === nextProps.isInactive &&
    prevProps.phone === nextProps.phone &&
    prevProps.email === nextProps.email &&
    prevProps.profilePhoto === nextProps.profilePhoto &&
    prevProps.finalScoring === nextProps.finalScoring &&
    prevProps.officeLocation === nextProps.officeLocation &&
    prevProps.person === nextProps.person &&
    prevProps.useConnectedActions === nextProps.useConnectedActions &&
    prevProps.selectionMode === nextProps.selectionMode &&
    prevProps.isSelected === nextProps.isSelected
  );
});
